import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { PlayerInput, EntityId, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';
import { DT } from '../config/simConfig';
import { BTN } from '../sim/input';
import { rngInt, type RngHolder } from '../math/rng';
import { inOwnBox, canGkPickup } from '../sim/gk';
import { ballLanding } from '../sim/aerial';
import { type BotMemory } from './actPlayer';
import { pickDistribution, clampToCone } from './gkDistribute';
import { trackPossessionSpell } from './actPlayer';
import { IDENTITY_PROFILE, aimNoise, type DifficultyProfile } from './difficulty';
import { inCeremony } from '../rules/phase';

// actKeeper (§8.1): position on the goal-centre↔ball arc at depth GK_DEPTH; commit a GK_DIVE when the
// predicted time-to-goal for an incoming shot falls to GK_REACT_TICKS + a sampled reaction delay; claim
// loose balls in the box; distribute from GK_HOLD to the widest-open teammate before the hold limit. Pure
// apart from drawing its reaction delay from the passed AI rng.

const angleTo = (from: Vec2, to: Vec2): number => Math.atan2(to.y - from.y, to.x - from.x);
const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const input = (mv: Vec2, aim: number, buttons: number, elev = 0): PlayerInput => ({ moveX: mv.x, moveY: mv.y, aimYaw: aim, aimElev: elev, buttons, emote: 0 });
const towards = (a: number): Vec2 => ({ x: Math.cos(a), y: Math.sin(a) });

/** True iff a human on the keeper's team is CALLING (an explicit "pass to me" order — G held). */
function callingHuman(state: MatchState, gk: PlayerState, humans: ReadonlySet<EntityId>): boolean {
  for (const h of humans) {
    const p = state.players[h];
    if (p && p.team === gk.team && p.id !== gk.id && !p.sentOff && p.calling) return true;
  }
  return false;
}

/** D46 Items 3+4: difficulty degrades the keeper the AI way — the sim's gk.SKILL (catch ladder) stays
 *  identical for everyone; the profile only slows his POSITIONING (chaseSpeedCap on every shuffle) and
 *  mistimes his commit (gkSkillMul on the dive threshold), plus aim noise on his distributions. IDENTITY
 *  (level 3) is a no-op. Enemy L1 (cap 0.25, gkSkill 0.25) ⇒ "the goal is empty. Almost." */
export function actKeeper(
  state: MatchState,
  id: EntityId,
  mem: BotMemory,
  rng: RngHolder,
  cfg: SimConfig,
  humans: ReadonlySet<EntityId> = new Set(),
  profile: DifficultyProfile = IDENTITY_PROFILE,
): PlayerInput {
  const hadKick = mem.kick !== null;
  let inp = actKeeperCore(state, id, mem, rng, cfg, humans, profile);
  if (!hadKick && mem.kick !== null) {
    mem.kick.aim += aimNoise(profile, rng, cfg); // mis-aim a charged distribution (feet-clear / goal-kick)
    // D62: RE-CLAMP to the safety cone. The noise was added AFTER actKeeperCore had already cone-clamped,
    // so it could rotate a release own-goal-ward and defeat the D46 guarantee outright. A mis-aimed
    // keeper is the intent; a keeper mis-aiming into his own net is the own-goal machine D46 closed.
    // D116 makes this load-bearing at EVERY level: the baseline now carries real aim error, where before
    // D116 levels 3–5 drew an exact zero and the clamp below never had anything to correct.
    const kp = state.players[id];
    if (kp) mem.kick.aim = clampToCone(mem.kick.aim, state.attackDir[kp.team], cfg.actions.gk.DIST_MIN_UPFIELD);
    // D116: no `passElevNoise` here on purpose — a keeper's distribution is a SOLVED punt/goal-kick with
    // its own elevation, and lofting it further is not "a scuffed pass", it is a ball into his own box.
    inp = { ...inp, aimYaw: mem.kick.aim };
  }
  if (profile.chaseSpeedCap < 1) inp = { ...inp, moveX: inp.moveX * profile.chaseSpeedCap, moveY: inp.moveY * profile.chaseSpeedCap };
  return inp;
}

function actKeeperCore(
  state: MatchState,
  id: EntityId,
  mem: BotMemory,
  rng: RngHolder,
  cfg: SimConfig,
  humans: ReadonlySet<EntityId>,
  profile: DifficultyProfile,
): PlayerInput {
  const p = state.players[id];
  if (!p || p.sentOff) return input({ x: 0, y: 0 }, 0, 0);
  const pitch = PITCH[state.mode.teamSize];
  const dir = state.attackDir[p.team];
  const ownGoalX = -dir * (pitch.length / 2);
  const halfGoal = pitch.goalWidth / 2;
  const ball = state.ball;
  trackPossessionSpell(state, p, mem, cfg); // D59: who back-passed to me this spell (no reflex return)
  const avoid = mem.receivedFrom; // spell-scoped — a back-pass means "switch the play"

  // D83/D84: during the CEREMONY the keeper-taker WALKS to the ball but never presses (the ball is
  // untakeable), and any stale pre-whistle distribution latch is DROPPED — so at ceremony end he
  // RE-EVALUATES `pickDistribution` against the world as it is THEN, not a target fixed at whistle time
  // ("he does not wait until players reposition and just kicks it to the opposing team").
  if (inCeremony(state, cfg) && state.restart !== null && state.restart.taker === id) {
    mem.kick = null;
    if (Math.hypot(ball.pos.x - p.pos.x, ball.pos.y - p.pos.y) > cfg.actions.kick.REACH * 0.75) {
      const a = angleTo(p.pos, ball.pos);
      return input(towards(a), a, 0); // step onto the ball, no press
    }
    return input({ x: 0, y: 0 }, angleTo(p.pos, ball.pos), 0); // wait at the ball, facing it
  }

  // continue an in-progress charged kick (D38 — same latch as the outfield bots: hold to target, release)
  if (p.charge !== null && mem.kick !== null) {
    const k = mem.kick;
    const elev = k.aimElev ?? 0; // D46: keeper lofted-punt elevation rides the input through release
    if (p.charge.ticks >= k.chargeTarget) {
      mem.kick = null; // release this tick → the kick fires
      return input({ x: 0, y: 0 }, k.aim, 0, elev);
    }
    return input({ x: 0, y: 0 }, k.aim, k.button, elev);
  }
  // D48: the press never latched (kick decided mid-action — edge-triggered buttons ignore a hold).
  // Pulse until the windup starts, while the ball is still ours to kick. Same fix as actPlayer.
  // D65-A: …but only while the ball is actually KICKABLE — the D62c gate actPlayer already has and the
  // keeper's twin branch was missing. A keeper who latched a mem.kick pre-whistle (his GOAL_CELEBRATION
  // distribution decision) then stood as the goal-kick TAKER used to pulse a kick at a ball 6–10 m away
  // forever — the sim's REACH gate silently swallowed every press. Not kickable ⇒ fall through to the
  // dead-ball taker branch below, which walks him onto the ball and kicks.
  const deadBallNow = (state.phase === 'KICKOFF' || state.phase === 'RESTART_PENDING') && state.restart !== null;
  const kickable = ball.possessor === id || Math.hypot(ball.pos.x - p.pos.x, ball.pos.y - p.pos.y) <= cfg.actions.kick.REACH;
  if (mem.kick !== null && kickable && (ball.possessor === id || (deadBallNow && state.restart!.taker === id))) {
    const k = mem.kick;
    return input({ x: 0, y: 0 }, k.aim, state.tick % 2 === 0 ? k.button : 0, k.aimElev ?? 0);
  }
  mem.kick = null;

  // distributing: aim at the distribution target (calling human → nearest open human → widest-open) and
  // release before the hold limit (T-AI-012)
  if (p.action === 'GK_HOLD' && ball.possessor === id) {
    const d = pickDistribution(state, p, humans, cfg, 'open', avoid); // D46 cone-safe; D59: never straight back to the feeder
    // D43: never gift an instant counter — hold until the press has receded (HOLD_SAFE_DIST) or…
    let oppNear = Infinity;
    for (const o of state.players) {
      if (o.team === p.team || o.sentOff) continue;
      oppNear = Math.min(oppNear, Math.hypot(o.pos.x - p.pos.x, o.pos.y - p.pos.y));
    }
    const safe = oppNear > cfg.actions.gk.HOLD_SAFE_DIST;
    const forced = p.actionTicks >= cfg.ai.GK_HOLD_LIMIT - 12; // beat the sim's auto-limit
    // D44 Item 6: a CALLING human is an explicit order — distribute as soon as the settle window passes.
    const ordered = callingHuman(state, p, humans);
    // D46 Item 5: …OR the hold-min has elapsed. A lofted punt always qualifies, so HOLD_MIN_TICKS breaks
    // the GK_HOLD_STANDOFF deadlock (a camped press no longer forces a late flat ball into the camp).
    const release = p.actionTicks >= cfg.actions.gk.SETTLE_TICKS && (safe || forced || ordered || p.actionTicks >= cfg.actions.gk.HOLD_MIN_TICKS);
    // D136: a GK HAND-distribution skipped BOTH the noise wrapper and the soft-lock, so it was the only
    // pass in the game that was perfect at every level. One draw on the release tick, then RE-CLAMPED
    // into the D46 safety cone: the noise may scuff the ball, never turn it toward his own goal.
    const aim = release ? clampToCone(d.aim + aimNoise(profile, rng, cfg), state.attackDir[p.team], cfg.actions.gk.DIST_MIN_UPFIELD) : d.aim;
    return input({ x: 0, y: 0 }, aim, release ? d.button : 0, d.aimElev);
  }
  // mid-dive/smother: inputs are ignored, just hold
  if (p.action === 'GK_DIVE' || p.action === 'GK_SMOTHER') return input({ x: 0, y: 0 }, p.facing, 0);

  // D38 dead-ball discipline: on a pending restart the keeper either TAKES it (his goal kick) or holds
  // his arc — he never chases/picks a dead ball that isn't his.
  const deadBall = (state.phase === 'KICKOFF' || state.phase === 'RESTART_PENDING') && state.restart !== null;
  if (deadBall && state.restart!.taker === id) {
    // D62: fetch the BALL, not the spot — the sim gates the kick on the ball's real position (see
    // actPlayer). A keeper taking a goal kick whose ball is not on the spot used to press forever.
    if (Math.hypot(ball.pos.x - p.pos.x, ball.pos.y - p.pos.y) > cfg.actions.kick.REACH * 0.75) {
      const a = angleTo(p.pos, ball.pos);
      return input(towards(a), a, 0); // step onto the ball
    }
    const d = pickDistribution(state, p, humans, cfg, 'nearest'); // D46 cone-safe; D55: a restart is a lay-off to the NEXT man
    mem.kick = { button: d.button, aim: d.aim, chargeTarget: d.chargeTarget, aimElev: d.aimElev };
    return input({ x: 0, y: 0 }, d.aim, d.button, d.aimElev);
  }

  // D38: ball at the keeper's FEET (box-exit putdown / stray claim) — clear it upfield NOW. The old
  // fall-through walked him to his positioning arc with the ball glued: the own-goal machine.
  // D39: under a RUSH (opponent within CLEAR_PRESSURE_DIST) it becomes a first-time PANIC clearance —
  // tap SHOOT toward the emptier flank, 2-tick charge, no aiming ceremony. Defusing danger wins.
  if (!deadBall && ball.possessor === id) {
    let oppNear = Infinity;
    let oppY = 0;
    for (const o of state.players) {
      if (o.team === p.team || o.sentOff) continue;
      const d = Math.hypot(o.pos.x - p.pos.x, o.pos.y - p.pos.y);
      if (d < oppNear) {
        oppNear = d;
        oppY = o.pos.y;
      }
    }
    if (oppNear < cfg.ai.CLEAR_PRESSURE_DIST) {
      const flankY = (oppY >= p.pos.y ? -1 : 1) * (pitch.width / 2) * 0.6; // away from the rusher
      // D46: keep the 2-tick panic blast but route its aim through the safety cone (never own-goal-ward).
      const aim = clampToCone(angleTo(p.pos, { x: dir * pitch.length * 0.25, y: flankY }), dir, cfg.actions.gk.DIST_MIN_UPFIELD);
      mem.kick = { button: BTN.SHOOT, aim, chargeTarget: 2 };
      return input({ x: 0, y: 0 }, aim, BTN.SHOOT);
    }
    const d = pickDistribution(state, p, humans, cfg, 'open', avoid); // D46 cone-safe feet-clear; D59: not back to the feeder
    mem.kick = { button: d.button, aim: d.aim, chargeTarget: d.chargeTarget, aimElev: d.aimElev };
    return input({ x: 0, y: 0 }, d.aim, d.button, d.aimElev);
  }

  // D18 keeper hands: pick up a loose ball in reach; smother a dribbler whose ball is at the feet
  if (!deadBall && canGkPickup(state, p, cfg)) {
    return input({ x: 0, y: 0 }, angleTo(p.pos, ball.pos), BTN.TACKLE);
  }
  if (!deadBall) {
    const carrier = ball.possessor !== null ? state.players[ball.possessor] : undefined;
    const ballDist = Math.hypot(ball.pos.x - p.pos.x, ball.pos.y - p.pos.y);
    if (carrier && carrier.team !== p.team && ball.gkLock !== id) {
      // D86: widened smother trigger — a carried ball at close range in the box is SMOTHERED (the
      // counter-verb to "dribbling right past him"; was gated at 0.85× and let a driven dribble slip).
      if (inOwnBox(state, p) && ballDist <= cfg.actions.gk.SMOTHER_TRIGGER) {
        const a = angleTo(p.pos, ball.pos);
        return input(towards(a), a, BTN.TACKLE); // contextual press ⇒ GK_SMOTHER in the sim (D18)
      }
      // D86 1v1 close-down: a carrier BEARING DOWN on goal (near, in front, roughly central) ⇒ come OFF
      // the line to MEET the ball, narrowing the shot angle, and the widened smother above wins it in
      // range. The keeper attacks a point just goal-SIDE of the ball (advancing toward it, never
      // retreating), clamped to a max depth off the line so he can't be chipped/rounded. This is what the
      // old "hold the arc until it's in the box" lacked — a straight dribble could stroll in before the
      // box. CORE at all levels; chaseSpeedCap already scales the advance speed, so a dumb keeper closes
      // SLOWER (level 1 stays beatable, level ≥ 2 punishes a straight dribble-past).
      const g1 = cfg.ai.gk1v1;
      const bx = ball.pos.x - ownGoalX;
      const by = ball.pos.y;
      const distBallGoal = Math.hypot(bx, by);
      const inFront = dir * bx > 0; // the ball is on the pitch side of the goal line
      const central = Math.abs(by) < pitch.penaltyBoxHalfWidth * g1.WIDTH_MUL;
      if (inFront && central && distBallGoal < g1.TRIGGER_DIST) {
        // a point ~one smother-radius goal-side of the ball, clamped to COMMIT_MAX off the line so he
        // can't be chipped/rounded — he comes out to MEET a central/near-central dribbler and smother.
        const goalSide = Math.min(cfg.actions.gk.SMOTHER_TRIGGER * 0.7, distBallGoal);
        const ux = bx / (distBallGoal || 1);
        const uy = by / (distBallGoal || 1);
        let tx = ball.pos.x - ux * goalSide;
        let ty = ball.pos.y - uy * goalSide;
        const depth = dir * (tx - ownGoalX);
        if (depth > g1.COMMIT_MAX) { tx = ownGoalX + dir * g1.COMMIT_MAX; } // clamp the come-out (chip/round guard)
        ty = clamp(ty, -halfGoal - 1.5, halfGoal + 1.5); // may edge past a post to cut the angle
        const face = angleTo(p.pos, ball.pos);
        return input(towards(angleTo(p.pos, { x: tx, y: ty })), face, 0);
      }
    }
  }

  // D88: defending a PENALTY ⇒ the keeper GUESSES a side (±1, seeded once) — a point-blank spot-kick can't
  // be reacted to, so he commits to a corner and is right ~half the time (a real penalty is a real threat).
  if (state.restart?.kind === 'PENALTY' && state.restart.team !== p.team && mem.gkPenaltyGuess === null) {
    mem.gkPenaltyGuess = rngInt(rng, 2) === 0 ? -1 : 1;
  }

  // incoming shot: ball loose, moving toward the own goal
  // D125: hysteresis on "a shot is coming at me". A single threshold at −2 m/s means a ball hovering
  // near it flips the keeper between the shot-tracking branch and the arc-hold branch every tick — and
  // those two branches want DIFFERENT y targets, which is the 180° slew that reads as a teleport.
  const closing = dir * ball.vel.x;
  const live = !deadBall && ball.possessor === null;
  mem.gkApproaching = live && (mem.gkApproaching ? closing < cfg.ai.GK_APPROACH_EXIT : closing < -2);
  const approaching = mem.gkApproaching;
  if (approaching && inOwnBox(state, p)) {
    const speedX = Math.abs(ball.vel.x);
    // ticks until the ball reaches the keeper's own x-line, and the ball y at that moment
    const ticksToKeeper = Math.max(0, Math.abs(ball.pos.x - p.pos.x) / (speedX * DT));
    // D88: on a penalty, dive to the GUESSED corner (not the predicted crossing — he can't read a spot kick)
    // D125: `ticksToKeeper` blows up as speedX → 0, and the raw prediction then saturates to a POST —
    // which is precisely the "stands on one edge, then teleports to the other" the report describes.
    // Blend the prediction toward the plain arc target by how confident the read is: a hard, fast shot
    // is trusted, a slow ambiguous roller holds the arc.
    const confidence = Math.min(1, speedX / cfg.ai.GK_SHUFFLE_SPEED);
    const predicted = ball.pos.y + ball.vel.y * ticksToKeeper * DT;
    const arcY = ball.pos.y * 0.8;
    const yAtKeeper = mem.gkPenaltyGuess !== null ? mem.gkPenaltyGuess * halfGoal * 0.8 : arcY + (predicted - arcY) * confidence;
    const face = angleTo(p.pos, { x: ball.pos.x, y: ball.pos.y });
    if (mem.gkReact === null) mem.gkReact = rngInt(rng, cfg.ai.reactionDelayJitter + 1);
    // commit the dive late so the ball meets the catch capsule early in the dive (window opens tick 2);
    // until then, shuffle hard to pre-position on the predicted crossing so the dive is short.
    // D46: gkSkillMul mistimes a worse keeper's commit (< 1 ⇒ dives too late; > 1 ⇒ sharper). SIM catch
    // quality (gk.SKILL) is untouched — this is pure AI positioning.
    // D87: the shuffle is now speed-capped (below), so the keeper covers less ground before committing —
    // commit the dive slightly EARLIER (a longer, VISIBLE dive covers the rest) so the save rate holds.
    const commit = (cfg.ai.GK_REACT_TICKS + mem.gkReact) * profile.gkSkillMul; // ≈ 6–8 ticks-to-keeper (was −2)
    if (ticksToKeeper <= commit && Math.abs(yAtKeeper) < halfGoal + 0.4) {
      const side = yAtKeeper < p.pos.y ? BTN.GK_DIVE_L : BTN.GK_DIVE_R; // dive toward the residual offset
      return input({ x: 0, y: 0 }, p.facing, side);
    }
    // D87: CAP the lateral pre-dive shuffle at GK_SHUFFLE_SPEED — a human slide, not a full-pace warp
    // across the mouth in a few snapshots ("teleport parry"). The ballistic DIVE (DIVE_SPEED) covers the rest.
    const ty = clamp(yAtKeeper, -halfGoal, halfGoal);
    const mv = towards(angleTo(p.pos, { x: p.pos.x, y: ty }));
    const shuffleMag = Math.min(1, cfg.ai.GK_SHUFFLE_SPEED / cfg.movement.MAX_SPEED);
    return input({ x: mv.x * shuffleMag, y: mv.y * shuffleMag }, face, 0); // shuffle purely laterally, capped
  }
  mem.gkReact = null; // no live shot
  if (state.restart === null || state.restart.kind !== 'PENALTY') mem.gkPenaltyGuess = null; // penalty over — reset the guess

  // D111 (HB-B) — ANSWER THE CROSS. Every approach test above this line is horizontal-only, so a ball
  // sailing over the keeper's 1.8 m capsule read as "not near me" for its entire flight and he stood on
  // his arc while it dropped onto a striker's head: a cross was free. He can't jump (out of scope for
  // v1), so his answer is positioning — get UNDER the landing point and let the existing catch/pickup
  // ladder do the rest when the ball descends into reach. Only for a delivery landing in his own box.
  if (!deadBall && ball.possessor === null && ball.pos.z > cfg.possession.CLAIM_Z) {
    const land = ballLanding(ball, cfg, state.conditions);
    const theirs = ball.lastTouch !== null && ball.lastTouch.team !== p.team;
    if (land !== null && theirs) {
      const depthIn = dir * (land.x - ownGoalX); // how far into the pitch from our line the ball drops
      if (depthIn >= 0 && depthIn <= pitch.penaltyBoxDepth && Math.abs(land.y) <= pitch.penaltyBoxHalfWidth) {
        const face = angleTo(p.pos, { x: ball.pos.x, y: ball.pos.y }); // eyes on the ball, feet to the spot
        const d = Math.hypot(p.pos.x - land.x, p.pos.y - land.y);
        // D125: capped like the pre-dive shuffle. D87 capped ONE of the four branches that move a
        // keeper; this is another, and at full MAX_SPEED it crosses the mouth in a couple of snapshots.
        const mv = d > 0.4 ? towards(angleTo(p.pos, land)) : { x: 0, y: 0 };
        const cap = Math.min(1, cfg.ai.GK_SHUFFLE_SPEED / cfg.movement.MAX_SPEED);
        return input({ x: mv.x * cap, y: mv.y * cap }, face, 0);
      }
    }
  }

  // loose ball in the box → go claim it (never a pending dead ball — D38)
  if (!deadBall && ball.possessor === null && inOwnBox(state, p) && Math.hypot(ball.pos.x - p.pos.x, ball.pos.y - p.pos.y) < pitch.penaltyBoxDepth) {
    return input(towards(angleTo(p.pos, { x: ball.pos.x, y: ball.pos.y })), angleTo(p.pos, ball.pos), 0);
  }

  // default: hold the goal-centre↔ball arc at depth, tracking ball y within the goal mouth
  const depth = cfg.ai.GK_DEPTH * (pitch.length / PITCH[11].length);
  const tx = ownGoalX + dir * depth;
  const ty = clamp(ball.pos.y * 0.8, -halfGoal, halfGoal);
  const face = angleTo(p.pos, { x: ball.pos.x, y: ball.pos.y });
  const d = Math.hypot(p.pos.x - tx, p.pos.y - ty);
  // D125: the last — and worst — uncapped branch. A hard `d > 0.4` is an oscillator at the boundary
  // (step in, overshoot, step back), and at MAX_SPEED each of those steps is a lunge. Hysteresis:
  // start moving above the band's top, stop below its bottom, and shuffle rather than sprint.
  const [stop, go] = cfg.ai.GK_HOLD_BAND;
  mem.gkHolding = mem.gkHolding ? d > stop : d > go;
  if (!mem.gkHolding) return input({ x: 0, y: 0 }, face, 0);
  const mv = towards(angleTo(p.pos, { x: tx, y: ty }));
  const cap = Math.min(1, cfg.ai.GK_SHUFFLE_SPEED / cfg.movement.MAX_SPEED);
  return input({ x: mv.x * cap, y: mv.y * cap }, face, 0);
}
